import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';

import { updateStatement, showImyTreeWithholding, getAllocationStatementFileByAccount, publishStatement,
  showEffectiveDateConfirmation, hideEffectiveDateConfirmation
} from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import { FormViewButton } from 'components/Common/Buttons/FormViewButton';
import FormTypes from 'common/Forms/FormTypes';
import ValidationInspector from 'common/Validation/ValidationInspector';
import EffectiveDateConfirmationModal from 'components/Modals/EffectiveDateConfirmationModal';

const StatementActionsPanel = ({ accountId, statement, structure, validationResult, className, expandTier, statementVersion, versionWindows }) => {
  const fields = {
    name: 'Name',
    description: 'Description'
  };

  const [statementName, setStatementName] = useState(statement?.statementName);
  const [statementDescription, setStatementDescription] = useState(statement?.statementDescription);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [rejectionOwner, setRejectionOwner] = useState(null);
  const [warningOwner, setWarningOwner] = useState(null);
  const [formRef, setFormRef] = useState(null);
  const { isSavingStatement, isPublishingStatement, isShowingEffectiveDateConfirmation } = useSelector(state => state && state.withholdingStatement);
  const { permissions } = useSelector(state => state && state.auth);

  const showingVersion = statementVersion !== null && typeof statementVersion !== 'undefined';
  const statementRecipients = (statement && (statement.beneficialOwners || statement.accounts)) || [];
  const canEdit = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]) && !showingVersion;
  const canDownloadPermission = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrDownloadWhs]);

  const isValid = !validationResult || validationResult.valid;
  const hasWarnings = ValidationInspector.hasWarnings(validationResult) || (validationResult?.lines && validationResult.lines.some(x => ValidationInspector.hasWarnings(x)));
  const hasRejections = ValidationInspector.hasRejections(validationResult) || (validationResult?.lines && validationResult.lines.some(x => ValidationInspector.hasRejections(x)));

  const dispatch = useDispatch();

  const findNearestImy = (imyLayer, guid) => {
    if (imyLayer.guid === guid || (imyLayer.recipients && imyLayer.recipients.find(x => x.guid === guid && !(x.recipients?.length >= 0)))) {
      return imyLayer;
    }

    const layer = imyLayer.recipients.find(x => x.recipients && findNearestImy(x, guid));
    return layer && findNearestImy(layer, guid);
  };

  const findFirstInStructure = (item, lineArray) => {
    const rowDetails = item?.ownerDetails || item;
    if (rowDetails && Object.prototype.hasOwnProperty.call(rowDetails, 'lineNo') && lineArray.indexOf(rowDetails.lineNo) > -1) {
      return item;
    }

    return item.recipients && item.recipients.find(x => findFirstInStructure(x, lineArray));
  };

  const buildValidationLinesMap = (fnFilter, pValidationResult) => pValidationResult
    && !fnFilter(pValidationResult) && pValidationResult?.lines?.reduce((prev, curr) => { if (fnFilter(curr)) { prev.push(curr.lineNumber); } return prev; }, []);

  React.useEffect(() => () => dispatch(hideEffectiveDateConfirmation()), []);

  React.useEffect(() => {
    const statementNameChanged = statementName != null && statementName !== statement.statementName;
    const statementDescriptionChanged = statementDescription != null && statementDescription !== statement.statementDescription;
    setIsSaveDisabled(!statementNameChanged && !statementDescriptionChanged);
  }, [statementName, statementDescription, isSavingStatement]);

  React.useEffect(() => {
    const warningsMap = buildValidationLinesMap(ValidationInspector.hasWarnings, validationResult);
    const rejectionsMap = buildValidationLinesMap(ValidationInspector.hasRejections, validationResult);

    const firstWarning = warningsMap && findFirstInStructure(structure, warningsMap);
    const firstRejection = rejectionsMap && findFirstInStructure(structure, rejectionsMap);

    setRejectionOwner(firstRejection && findNearestImy(structure, firstRejection.guid));
    setWarningOwner(firstWarning && findNearestImy(structure, firstWarning.guid));
  }, [validationResult, structure]);

  const onFormSubmit = (event) => {
    event.preventDefault();
    if (!isSaveDisabled) {
      const obj = {
        description: {
          name: statementName,
          description: statementDescription
        }
      };

      dispatch(updateStatement(accountId, obj));
    }
  };

  const onFormReset = (event) => {
    setStatementName(statement.statementName);
    setStatementDescription(statement.statementDescription);
    formRef.reset();
    event.target.blur();
  };

  const displayImy = (formaccountId, versionNumber) => {
    dispatch(showImyTreeWithholding(formaccountId, versionNumber));
  };

  const getAllocationFile = (whdAccountId) => {
    dispatch(getAllocationStatementFileByAccount(whdAccountId));
  };

  const onFieldChange = (e, value, field) => {
    switch (field) {
      case fields.name:
        setStatementName(value.trim());
        break;
      case fields.description:
        setStatementDescription(value.trim());
        break;
      default:
        break;
    }
  };

  const onConfirmEffectiveDate = () => {
    dispatch(showEffectiveDateConfirmation());
  };

  const onPublishStatement = (versionWindow) => {
    dispatch(publishStatement(accountId, versionWindow));
    dispatch(hideEffectiveDateConfirmation());
  };

  const onCancelPublishStatement = () => {
    dispatch(hideEffectiveDateConfirmation());
  };

  const buildFormObject = (item) => {
    if (!item) {
      return null;
    }
    const formType = item && item.formType && FormTypes.getByValue(item.formType);
    const formObject = {
      accountId: item.accountId,
      formId: item.formId,
      formType: formType && formType.code,
      workflowStatus: item.workflowStatus,
      workflowUsername: item.workflowUsername ? item.workflowUsername : 'N/A',
      workflowUserFullName: item.workflowUserFullName
    };
    return formObject;
  };

  const checkPageForClass = classToFind => document.querySelector(`${ classToFind } .icon:not(.statement-panel):not(#statement-actions-panel)`);

  const findClosestWithId = (element) => {
    if (!element) {
      return null;
    }

    if (element.id && element.id !== '') {
      return element;
    }

    if (element.closest) {
      return element.closest('[id]');
    }

    return findClosestWithId(element.parentElement);
  };

  const getAncestorDivWithId = (classToFind) => {
    const div = checkPageForClass(classToFind);

    return (div && findClosestWithId(div)) || null;
  };

  const scrollIntoView = async (div) => {
    const navbarHeight = 90;
    if (!div) {
      return;
    }
    const scrollToY = div.offsetTop - navbarHeight;
    if (div.closest) {
      window.scrollTo({ top: scrollToY, behavior: 'smooth' });
    } else {
      let y = window.screenTop || 0;
      const direction = y < scrollToY ? 1 : -1;
      const smoothScroll = () => setTimeout(() => {
        y += 40 * direction;
        y = direction === 1 ? Math.min(y, scrollToY) : Math.max(y, scrollToY);
        window.scrollTo(0, y);
        if ((direction === 1 && y < scrollToY) || (direction === -1 && y > scrollToY)) {
          smoothScroll();
        }
      }, 30);

      smoothScroll();
    }
  };

  const scrollToValidationItem = async (validationClass, ownerObject) => {
    const identifier = ownerObject?.guid || ownerObject?.basicDetails?.name || ownerObject?.ownerDetails?.name;
    if (identifier) {
      await expandTier(identifier);
    }

    const div = getAncestorDivWithId(validationClass);
    scrollIntoView(div);
  };

  const scrollToFirstWarning = () => scrollToValidationItem('.validation-warning', warningOwner);
  const scrollToFirstRejection = () => scrollToValidationItem('.validation-error', rejectionOwner);

  return (<div id="statement-actions-panel" className={ `panel p-2 my-2 imy-card imy-layer statement-actions-panel ${ className }${ (!isValid || hasWarnings || hasRejections) ? ' validation-error-panel' : '' }` }>
    <div className="columns mx-0">
      <div className="col-6">
        <div className="panel-header no-border"><h2 className="value">Withholding Statement</h2></div>
        <div className="panel-body columns">
          { canEdit && <form id="statement-name-form" onSubmit={ onFormSubmit } className="col-12 mr" ref={ el => setFormRef(el) }>
            <input type="text" className="form-input mb-2" placeholder="Statement Name" defaultValue={ statement.statementName } onChange={ (e, value) => onFieldChange(e, e.target.value, fields.name) } data-testid="whd-statement-details-name" />
            <textarea
              className="form-input my-2"
              placeholder="Type a statement description here"
              rows="6"
              onChange={ (e, value) => onFieldChange(e, e.target.value, fields.description) }
              defaultValue={ statement.statementDescription || '' }
            />
            <div className="button-row mt-2 pt-2">
              <button type="button" className="btn btn-secondary mr-2" onClick={ onFormReset }>Discard Changes</button>
              <button type="submit" disabled={ isSaveDisabled || isSavingStatement || isPublishingStatement ? 'disabled' : '' } className="btn btn-primary ml-2" data-testid="whd-statement-details-save">Save</button>
            </div>
          </form> }
          { !canEdit && (statementName || statementDescription) && <div className="col-12">
            <h3 className="mb-1">{ statementName || <i>Statement Name Empty</i> }</h3>
            <p>{ statementDescription || <i>Statement Description Empty</i> }</p>
          </div> }
          { (!isValid || hasRejections || hasWarnings) && <div className="col-12 validation-container mt-2rem">
            <div className="validation-summary">
              { (!isValid || hasRejections) && <button
                className="validation-error text-link statement-panel"
                onClick={ () => scrollToFirstRejection() }
                type="button"
              >
                <span className="icon icon-cross icon-cross-circle mr-2 statement-panel" />The statement contains errors.
                </button> }
              { hasWarnings && <button
                className="validation-warning text-link statement-panel"
                onClick={ () => scrollToFirstWarning() }
                type="button"
              >
                <span className="icon icon-flag solid mr-2 statement-panel" />The statement contains warnings.
                </button> }
            </div>
          </div> }
        </div>
      </div>
      <div className="col-3 align-vertical-center">
        <div className="col-4">
          { statement.formId > 0 && AccessControl.hasPermission(permissions, [ActivityPerms.scannedView, ActivityPerms.scannedEdit, ActivityPerms.scannedValidate]) && <FormViewButton
            permissions={ permissions }
            formId={ statement.formId }
            form={ buildFormObject(structure.basicDetails) }
            content={ <React.Fragment><i className="icon icon-2x icon-apps mr-2" />IMY Form</React.Fragment> }
            className="btn btn-secondary"
            title="View IMY Form"
          /> }
        </div>
        <div className="col-4">
          { AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]) && <button
            className="btn btn-secondary"
            type="button"
            onClick={ () => displayImy(accountId, statementVersion) }
          ><i className="icon icon-treeview mr-2" />IMY Tree</button> }
        </div>
        <div className="col-4 relative">
          { statement.hasOriginalStatement && canDownloadPermission && !showingVersion && <button
            className="btn btn-secondary"
            type="button"
            onClick={ () => getAllocationFile(accountId) }
          ><i className="icon icon-2x icon-download mr-2" />Download Original</button> }
        </div>
      </div>
      <div className="col-3 allocation-column align-vertical-center">
        <div className="allocation-container">
          <div className="allocation-title">
            Total Owners
          </div>
          <div className="allocation-value">
            { statementRecipients.length }
          </div>
          <div className="allocation-title">
            Total Tier Ownership
          </div>
          <div className="allocation-value">
            { statementRecipients.reduce((p, c) => (p || 0) + (c.allocationIncomeRate * 100), 0).toFixed(statement.decimalPlacePrecision) }%
          </div>
          { !showingVersion && <div className="publish-btn">
            <button type="button" disabled={ isSavingStatement || isPublishingStatement ? 'disabled' : '' } className="btn" onClick={ onConfirmEffectiveDate }>Publish Statement</button>
          </div> }
        </div>
      </div>
    </div>
    { isShowingEffectiveDateConfirmation && <EffectiveDateConfirmationModal
      key="confirmEffectiveDateModal"
      isShown={ isShowingEffectiveDateConfirmation }
      onConfirm={ versionWindow => onPublishStatement(versionWindow) }
      onCancel={ () => onCancelPublishStatement() }
      title="Publish Statement"
      confirmTitle="Confirm Effective Date and Publish Statement"
      structure={ structure }
      defaultEffectiveFrom={ statement?.effectiveDate && statement.effectiveDate }
      defaultEffectiveTo={ statement?.effectiveTo && statement.effectiveTo }
      versionWindows={ versionWindows }
    /> }
  </div>);
};

StatementActionsPanel.propTypes = {
  statement: PropTypes.shape({
    beneficialOwners: PropTypes.arrayOf(PropTypes.shape({})),
    accounts: PropTypes.arrayOf(PropTypes.shape({})),
    decimalPlacePrecision: PropTypes.number,
    statementDescription: PropTypes.string,
    statementName: PropTypes.string,
    formId: PropTypes.number,
    hasOriginalStatement: PropTypes.bool,
    type: PropTypes.number,
    effectiveDate: PropTypes.string,
    effectiveTo: PropTypes.string
  }).isRequired,
  validationResult: PropTypes.shape({
    valid: PropTypes.bool,
    rejections: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
    warnings: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
    lines: PropTypes.arrayOf(PropTypes.shape({
      warnings: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
      rejections: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
    }))
  }),
  accountId: PropTypes.number.isRequired,
  className: PropTypes.string,
  structure: PropTypes.shape({
    basicDetails: PropTypes.shape({})
  }),
  expandTier: PropTypes.func.isRequired,
  statementVersion: PropTypes.string,
  versionWindows: PropTypes.arrayOf(PropTypes.shape()),
};

StatementActionsPanel.defaultProps = {
  className: null,
  structure: null,
  validationResult: null,
  statementVersion: null,
  versionWindows: null
};

export default StatementActionsPanel;
